
import { Injectable } from '@angular/core';
import { Account, AccountType, Expense } from '../models/index';
import { getSupabase, isSupabaseConfigured } from '../firebase';
import { SupabaseClient } from '@supabase/supabase-js';

const ACCOUNT_TABLE = 'accounts';
const EXPENSE_TABLE = 'expenses';

@Injectable({ providedIn: 'root' })
export class AccountingService {
  private supabase: SupabaseClient | null = getSupabase();

  // --- ACCOUNTS (Chart of Accounts) ---
  private toAccountDB(account: Partial<Account>): any {
    return {
      id: account.id,
      store_id: account.storeId,
      name: account.name,
      type: account.type,
      description: account.description,
    };
  }
  
  private fromAccountDB(dbData: any): Account {
     return {
      id: dbData.id,
      storeId: dbData.store_id,
      name: dbData.name,
      type: dbData.type,
      description: dbData.description,
      createdAt: dbData.created_at
     } as Account;
  }

  async getAccounts(): Promise<Account[]> {
    if (!isSupabaseConfigured() || !this.supabase) { return []; }
    const { data, error } = await this.supabase.from(ACCOUNT_TABLE).select('*');
    if (error) throw error;
    return (data || []).map(this.fromAccountDB);
  }
  
  async addAccount(accountData: Omit<Account, 'id' | 'createdAt' | 'storeId'>): Promise<Account> {
    if (!isSupabaseConfigured() || !this.supabase) { throw new Error("Database not connected"); }
    const dbPayload = this.toAccountDB(accountData);
    delete (dbPayload as any).id;
    const { data, error } = await this.supabase.from(ACCOUNT_TABLE).insert(dbPayload).select().single();
    if (error) throw error;
    return this.fromAccountDB(data);
  }

  async updateAccount(updatedAccount: Account): Promise<Account> {
    if (!isSupabaseConfigured() || !this.supabase) { throw new Error("Database not connected"); }
    const { id, ...accountData } = updatedAccount;
    const dbPayload = this.toAccountDB(accountData);
    const { data, error } = await this.supabase.from(ACCOUNT_TABLE).update(dbPayload).eq('id', id).select().single();
    if (error) throw error;
    return this.fromAccountDB(data);
  }

  async deleteAccount(accountId: string): Promise<void> {
    if (!isSupabaseConfigured() || !this.supabase) { throw new Error("Database not connected"); }
    const { error } = await this.supabase.from(ACCOUNT_TABLE).delete().eq('id', accountId);
    if (error) throw error;
  }

  // --- EXPENSES ---
  
  private fromExpenseDB(dbData: any): Expense {
      return {
          id: dbData.id,
          storeId: dbData.store_id,
          amount: dbData.amount,
          category: dbData.category,
          description: dbData.description,
          expenseDate: dbData.expense_date,
          createdAt: dbData.created_at
      } as Expense;
  }

  async getExpenses(month?: string): Promise<Expense[]> {
      if (!isSupabaseConfigured() || !this.supabase) { return []; }
      
      let query = this.supabase.from(EXPENSE_TABLE).select('*').order('expense_date', { ascending: false });
      
      if (month) {
          // month format 'YYYY-MM'
          const start = `${month}-01`;
          // Simple logic to get end of month
          const date = new Date(start);
          const end = new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().split('T')[0];
          query = query.gte('expense_date', start).lte('expense_date', end);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []).map(this.fromExpenseDB);
  }

  async addExpense(expenseData: Omit<Expense, 'id' | 'createdAt' | 'storeId'>): Promise<Expense> {
      if (!isSupabaseConfigured() || !this.supabase) { throw new Error("Database not connected"); }
      
      const payload = {
          amount: expenseData.amount,
          category: expenseData.category,
          description: expenseData.description,
          expense_date: expenseData.expenseDate
      };

      const { data, error } = await this.supabase.from(EXPENSE_TABLE).insert(payload).select().single();
      if (error) throw error;
      return this.fromExpenseDB(data);
  }

  async deleteExpense(expenseId: string): Promise<void> {
      if (!isSupabaseConfigured() || !this.supabase) { throw new Error("Database not connected"); }
      const { error } = await this.supabase.from(EXPENSE_TABLE).delete().eq('id', expenseId);
      if (error) throw error;
  }
}
